import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { faPlus, faSpinner } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { omit } from 'ramda'
import React, {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  forwardRef,
  useCallback,
  useState,
} from 'react'
import {
  Link,
  useInRouterContext,
  useNavigation,
  useResolvedPath,
} from 'react-router-dom'
import { twMerge } from 'tailwind-merge'
import { useDebouncedValue, useIsMobile } from '../../../hooks'
import { Tooltip } from '../../Text/Tooltip'
import { ApplicationRoutes, formatFrontendError } from '@unimpaired/utils'
import { NoticeText } from '../../Text'

type Variants =
  | 'unstyled'
  | 'raised'
  | 'neutral'
  | 'warn'
  | 'outline'
  | 'link'
  | 'link-warn'
  | 'nav-link'
  | 'outline-dark'
  | 'modal-button'
  | 'base'
  | 'soft-warn'
  | 'condensed'
  | 'modal-icon'
  | 'card-action'
  | 'card-outline'

type BaseProps = {
  /** This displays the button in a specific style */
  variant?: Variants
  debounce?: boolean
  iconPosition?: 'left' | 'right'
  iconOnlyOnMobile?: boolean
  iconClasses?: string
  icon?: IconProp
  type?: 'button' | 'submit'
  className?: string
  children?: React.ReactNode
  loading?: boolean
  tooltip?: Parameters<typeof Tooltip>[0]['tooltipContent']
  tooltipWrapperClasses?: string
  // Do NOT define this prop here. You cannot disable a link
  // Disabled is only available if the type resolves to button
  // disabled?: boolean
}

export type ButtonActionProps = BaseProps & {
  href?: never
  to?: never
} & ButtonHTMLAttributes<HTMLButtonElement>

export type ButtonExternalLinkProps = BaseProps & {
  href: string
  to?: never
} & AnchorHTMLAttributes<HTMLAnchorElement>

export type ButtonInternalLinkProps = BaseProps & {
  to: ApplicationRoutes | number
  href?: never
} & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'>

export type ButtonProps =
  | ButtonActionProps
  | ButtonInternalLinkProps
  | ButtonExternalLinkProps

export const Button = forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  ButtonProps
>(
  (
    // loading is destructured because otherwise it throws a weird type
    // warning that I don't understand the reason for
    { variant = 'base', debounce, className, loading: propsLoading, ...props },
    ref,
  ) => {
    // base styles for all variants
    // overridden by any conflicting tailwind styles specified in the variant or className strings

    // If we're in e.g. storybook don't run useNavigation as that throws errors
    const inRouterContext = useInRouterContext()
    const navigation = inRouterContext ? useNavigation() : undefined
    // resolve possible relative values '..' / '-1' to paths '/some/path'
    const resolvedPath = inRouterContext
      ? //@ts-expect-error types are wrong - doesn't allow relative number routing
        useResolvedPath(props.to ?? props.href ?? '')
      : undefined
    const navigatingToPathname = navigation?.location?.pathname
    const buttonTargetPathname =
      props.to || props.href ? resolvedPath?.pathname : undefined

    const variantsClasses: Record<Variants, string> = {
      unstyled: '',
      condensed: '',
      'soft-warn':
        'text-red-700 border border-red-700 shadow-sm hover:bg-red-600 hover:text-white  focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-500 disabled:bg-base-300 disabled:text-base-600 disabled:hover:text-base-600',
      base: 'disabled:bg-base-300 bg-base-0 dark:text-base-700 dark:bg-transparent disabled:text-base-600 disabled:hover:text-base-600 focus:outline-none shadow-sm hover:bg-primary-100 hover:text-primary-700 dark:hover:text-primary-400 border-base-300 dark:hover:bg-base-700 dark:border-base-800 border',
      raised:
        'border border-primary-600 bg-primary-500 dark:text-base-1000 text-base-0 shadow-sm hover:bg-primary-500  focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500 disabled:border-none disabled:bg-base-300 disabled:text-base-600 disabled:hover:text-base-600',
      neutral:
        'bg-base-300 text-base-1000 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-base-300 disabled:bg-base-300 disabled:text-base-600 disabled:hover:text-base-600',
      'nav-link':
        'text-indigo-600 hover:text-indigo-800 dark:text-pink-400 dark:hover:text-pink-600 px-0 text-md p-0',
      link: 'text-blue-400 hover:text-blue-500 px-0',
      'link-warn':
        'text-red-700 hover:text-red-600 px-0 disabled:bg-gray-100 disabled:px-1',
      outline:
        'rounded-tl-md rounded-md hover:border-blue-800 hover:bg-blue-100 hover:text-blue-800 w-full border border-blue-300 focus:outline-none flex items-center justify-center text-blue-800 disabled:bg-base-300 disabled:text-base-600 disabled:border-none disabled:cursor-not-allowed disabled:hover:border-none',
      warn: 'rounded bg-red-700 text-white shadow-sm hover:bg-red-600  focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-500 disabled:bg-base-300 disabled:text-base-600 disabled:hover:border-none',
      'outline-dark':
        'disabled:bg-base-300 disabled:text-base-600 text-base-300 dark:text-base-700 disabled:hover:text-base-300 group transition-all duration-150 ease-in-out focus:outline-none shadow-lg hover:bg-blue-100 hover:text-blue-700 border-gray-200 border',
      'modal-icon':
        ' rounded text-left transition space-x-3 px-4 w-full flex items-center py-4 text-sm font-semibold hover:bg-indigo-400 hover:text-white  focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500 ',
      'modal-button':
        'border-b w-full text-left px-4 py-2 text-sm flex items-center hover:text-white space-x-2 font-semibold hover:bg-indigo-400  focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500',
      'card-action': twMerge(
        'flex text-center min-w-fit w-0 bg-base-100 flex-1 py-4 px-2 sm:py-6 transform duration-150 hover:bg-base-200 items-center justify-center',
        'h-10 text-md font-medium text-indigo-600  hover:text-indigo-500 ',
        'dark:justify-left disabed:bg-inherit dark:bg-gray-800 dark:text-blue-400  dark:hover:text-cyan-200',
        'dark:hover:bg-base-800/10',
      ),
      'card-outline': twMerge(
        'transition relative max-w-2xl block',
        'w-full rounded-lg border-2 border-dashed border-gray-300 text-center',
        'hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500',
        'focus:ring-offset-2  bg-white dark:bg-text-white dark:bg-base-200',
        'py-4',
      ),
    }

    const getBaseClasses = () => {
      if (variant === 'unstyled') return ''
      if (variant === 'card-outline') return ''
      if (variant === 'card-action') return ''
      return twMerge(
        'relative flex space-x-2 items-center justify-center',
        'rounded-md px-4 py-2 text-blue-500 text-sm font-semibold',
        'transition-all duration-300 ease-in-out group',
        'disabled:border-none disabled:bg-base-300 disabled:text-base-600 disabled:hover:text-base-600 disabled:hover:cursor-not-allowed',
      )
    }

    const containerClassNames = twMerge(
      getBaseClasses(),
      variantsClasses[variant],
      className,
    )

    const [onClickRunning, setOnClickRunning] = useState(false)
    const [error, setError] = useState<string>()

    const onClick = async (onClickProps: any) => {
      setOnClickRunning(true)
      try {
        await props.onClick?.(onClickProps)
      } catch (err: unknown) {
        const message = formatFrontendError(err)
        setError(message)
        console.log(message)
        throw err
      } finally {
        setOnClickRunning(false)
      }
    }

    const [loading] = useDebouncedValue(
      propsLoading ||
        // if we're running an async click handler
        onClickRunning ||
        // or if the app is navigating to the location spec'd by this button
        (!!buttonTargetPathname &&
          buttonTargetPathname === navigatingToPathname),
      100,
    )

    // Disabled may not be present if type is not a button (cannot disable links)
    const isDisabled = loading || ('disabled' in props && props.disabled)

    // Memoize the tooltip layout to avoid unnecessary re-renders
    // and resetting the state inside the tooltip
    // react-compiler-migration: kept — TooltipLayout is rendered as a JSX component; an unstable identity would remount Tooltip and reset its internal state
    const TooltipLayout = useCallback(
      ({
        children,
        className,
      }: {
        children: React.ReactNode
        className?: string
      }) =>
        props.tooltip ? (
          <Tooltip
            className={className}
            tooltipContent={props.tooltip}
            disableTap>
            {children}
          </Tooltip>
        ) : (
          <>{children}</>
        ),
      [props.tooltip],
    )

    if (error && import.meta.env.DEV)
      return (
        <div className='absolute z-[10000000] top-20 left-10'>
          <NoticeText type='error' text={error}>
            <button onClick={() => setError(undefined)}>Reset</button>
          </NoticeText>
        </div>
      )

    if (isButton(props))
      // Have a fragment around a submit type button breaks triggering of the
      // form submit somehow and it's blowing my mind, so we're just going
      // to conditionally return the button by itself if no tooltip exists
      return props.tooltip ? (
        <TooltipLayout className={props.tooltipWrapperClasses}>
          <button
            {...omit(
              ['iconClasses', 'iconPosition', 'iconOnlyOnMobile'],
              props,
            )}
            // Default to 'button' — without this, the HTML default of
            // 'submit' fires the nearest enclosing <form>, causing a
            // page reload anywhere a <Button> is dropped inside a form.
            type={props.type ?? 'button'}
            className={containerClassNames}
            disabled={isDisabled}
            // @ts-expect-error can't figure out how to type the ref conditionally RN
            ref={ref}
            onClick={onClick}>
            <ButtonContent {...props} variant={variant} loading={loading} />
          </button>
        </TooltipLayout>
      ) : (
        <button
          {...omit(['iconClasses', 'iconPosition', 'iconOnlyOnMobile'], props)}
          // Default to 'button' — without this, the HTML default of
          // 'submit' fires the nearest enclosing <form>, causing a
          // page reload anywhere a <Button> is dropped inside a form.
          type={props.type ?? 'button'}
          className={containerClassNames}
          disabled={isDisabled}
          // @ts-expect-error can't figure out how to type the ref conditionally RN
          ref={ref}
          onClick={onClick}>
          <ButtonContent {...props} variant={variant} loading={loading} />
        </button>
      )

    if (isInternalLink(props))
      return (
        <TooltipLayout className={props.tooltipWrapperClasses}>
          <Link
            {...omit(
              ['iconClasses', 'iconPosition', 'iconOnlyOnMobile'],
              props,
            )}
            className={containerClassNames}
            aria-disabled={isDisabled}
            // @ts-expect-error can't figure out how to type the ref conditionally RN
            ref={ref}
            onClick={onClick}
            //@ts-expect-error
            to={props.to}>
            <ButtonContent {...props} variant={variant} loading={loading} />
          </Link>
        </TooltipLayout>
      )

    if (isExternalLink(props))
      return (
        <TooltipLayout className={props.tooltipWrapperClasses}>
          <a
            {...omit(
              ['iconClasses', 'iconPosition', 'iconOnlyOnMobile'],
              props,
            )}
            className={containerClassNames}
            aria-disabled={isDisabled}
            // @ts-expect-error can't figure out how to type the ref conditionally RN
            ref={ref}
            onClick={onClick}>
            <ButtonContent {...props} variant={variant} loading={loading} />
          </a>
        </TooltipLayout>
      )

    return null
  },
)

const ButtonContent = ({
  children,
  iconOnlyOnMobile,
  variant,
  ...props
}: Pick<
  ButtonProps,
  | 'variant'
  | 'children'
  | 'iconPosition'
  | 'iconClasses'
  | 'icon'
  | 'loading'
  | 'iconOnlyOnMobile'
>) => {
  const isMobile = useIsMobile()

  const getIconPosition = () => {
    if (props.iconPosition) return props.iconPosition
    if (variant === 'card-action') return 'right'
    return 'left'
  }

  const getIcon = () => {
    if (props.icon) return props.icon
    if (variant === 'card-outline') return faPlus
    return undefined
  }

  const iconClasses = twMerge(
    variant === 'card-outline' &&
      'mx-auto !h-12 !w-12 text-base-400/50 dark:text-gray-300',
    props.iconClasses,
  )

  const textClasses = twMerge(
    'grow',
    variant === 'card-outline' &&
      'mt-2 block text-sm font-semibold dark:text-white',
  )

  const iconPosition = getIconPosition()
  const icon = getIcon()

  return (
    <React.Fragment>
      {iconPosition === 'left' && (
        <ButtonIcon
          icon={icon}
          iconClasses={iconClasses}
          iconPosition={iconPosition}
          loading={props.loading}
        />
      )}
      {children && !(isMobile && iconOnlyOnMobile) && (
        <span className={textClasses}>{children}</span>
      )}
      {iconPosition === 'right' && (
        <ButtonIcon
          icon={icon}
          iconClasses={iconClasses}
          iconPosition={iconPosition}
          loading={props.loading}
        />
      )}
    </React.Fragment>
  )
}

const ButtonIcon = (
  props: Pick<ButtonProps, 'iconPosition' | 'loading' | 'iconClasses' | 'icon'>,
) => {
  const iconClasses = twMerge(
    `!h-4 !w-4`,
    props.iconPosition === 'left' ? '' : 'order-1',
    props.loading ? 'animate-spin' : '',
    props.iconClasses,
  )

  if (props.loading)
    return <FontAwesomeIcon icon={faSpinner} className={iconClasses} />

  if (props.icon)
    return <FontAwesomeIcon icon={props.icon} className={iconClasses} />

  return null
}

const isInternalLink = (props: ButtonProps): props is ButtonInternalLinkProps =>
  'to' in props && !!props.to
const isExternalLink = (props: ButtonProps): props is ButtonExternalLinkProps =>
  'href' in props && !!props.href
const isButton = (props: ButtonProps): props is ButtonActionProps =>
  !props.href && !props.to
